import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { staticRoutesWithCustomSidebar } from 'Deer/constants/staticRoutesWithCustomSidebar';
// import { useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import { useViewById } from 'components/Views/Hooks/dataHooks';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { REVIEW_CYCLE_STEPS_V1 } from 'reducers/reviewCreationSlice';
import {
  resetReviewManageSidepanelState,
  setGridColumns,
  setGridRows,
  setReviewCycleStage,
  useReviewManageSidepanelData,
} from 'reducers/reviewManageSidepanelSlice';
import { useTalentPlanningSidepanelData } from 'reducers/talentPlanningSidepanelSlice';
import { useSelectedView } from 'reducers/viewsSlice';
import useDeerL1PageBasedL3Routes from 'screens/performanceReview/hooks/useDeerL1PageBasedL3Routes';
import useDeerTeamBasedReviewRoute from 'screens/performanceReview/hooks/useDeerTeamBasedReviewRoute';
import {
  checkIntentLandingPagesFeatureEnabled,
  checkNewNavEnabled,
  checkSankeyChartEnabled,
} from 'utils/helpers/featureFlags';

import { AppNavigationOutlined, BarChart, Plus } from 'assets/assetsV1/iconComponents';
import addReviewV1 from 'assets/assetsV1/illustrations/newIllustrations/addReviewV1.svg';
import NoDataImg from 'assets/assetsV1/illustrations/noDataTrendLine.svg';

import { MODULE_KEYS_USER_PREFERENCE_PERSISTENCE } from 'constants/ProjectConstants';
import { getMatchPathIndexFromRoutesArray, getPersistenceKey, getQueryParams } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import { routes } from 'constants/routes';

import { getBarGraphData, getPerformanceGridData, getReviewCycleStage } from 'services/analytics/review';

import { Button } from 'uiToolkitV1/Button';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { ExpandableContainerProvider } from 'uiToolkitV1/ExpandableContainerProvider';
import { PageTabbedNavigation } from 'uiToolkitV1/PageTabbedNavigation';
import { PageTabbedNavigationV1 } from 'uiToolkitV1/PageTabbedNavigationV1';
import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextHeader3 } from 'uiToolkitV1/Typography';

import TabbedNavigationTitle from '../components/TabbedNavigationTitle';
import PerfCalibrationBarChart from './components/PerfCalibrationBarChart';
import ReviewManagePerformanceGrid from './components/ReviewManagePerformanceGrid';
import ReviewManageSummary from './components/ReviewManageSummary';
import { ReviewManageTableV1 } from './components/ReviewManageTable/ReviewManageTableV1';
import ReviewManageTimeline from './components/ReviewManageTimeline';
import ReviewsSankeyChart from './components/SankeyChart/SankeyChart';
import TrendLineChart from './components/TrendLineChart/TrendLineChart';
import REACT_QUERY_KEYS_REVIEWS_INSIGHTS from './components/TrendLineChart/constants/reactQueryKeysReviewsInsights';
import useReviewTrendLineChartData from './components/TrendLineChart/hooks/dataHooks';
import { useGetReviewCyclesAndCalibrationViews } from './dataHooks';

const StyledChartToggleBtn = styled(Button)`
  height: 35px;
  padding: 5px;
  color: #666666;

  & svg {
    color: #666666;
  }

  &:hover:enabled {
    background: #ffffff;
    color: #104f95;
  }

  &.active {
    background: #ffffff;
    color: #104f95;
  }
`;

// adding loader and empty state here because if i add this inside chart component toggle button will be
// visible while loading and no data state
// to-do extract out logic of charts in a separate component

// const EmptyStateComp = ({ performanceGridDataLoading }) => (
//   <PaperWithShadow p={4} h="345px">
//     {performanceGridDataLoading ? (
//       <SkeletonLoader />
//     ) : (
//       <EmptyScreen
//         illustrationSrc={NoDataImg}
//         isSmallTextSize
//         heading="During active review cycles, you can view your growth on the performance grid."
//       />
//     )}
//   </PaperWithShadow>
// );

function ReviewManageV1() {
  const [performanceGridData, setPerformanceGridData] = useState([]);

  const [performanceGridEmpDetails, setPerformanceGridEmpDetails] = useState([]);

  const [performanceGridNoRating, setPerformanceGridNoRating] = useState({});

  const [isDisplayPictures, setIsDisplayPictures] = useState(false);

  const [performanceGridDataLoading, setPerformanceGridDataLoading] = useState(false);

  const [gridOrChartToggle, setGridOrChartToggle] = useState('GRID');

  const [gridChartData, setGridChartData] = useState([]);

  const [reviewCycleListForSankeyChart, setReviewCycleListForSankeyChart] = useState([]);

  const [selectedReviewCycleForSankeyChart, setSelectedReviewCycleForSankeyChart] = useState(null);

  const queryClient = useQueryClient();
  const location = useLocation();

  const dispatch = useDispatch();
  const teamBasedReviewRoute = useDeerTeamBasedReviewRoute();
  const { l3Options: tabsDeer } = useDeerL1PageBasedL3Routes();

  const { selectedCalibrationView } = useTalentPlanningSidepanelData();

  const { tableResetCounter, selectedReviewCycle } = useReviewManageSidepanelData();

  const selectedCohort = useSelectedView();

  const { data: trendLineChartData, isLoading } = useReviewTrendLineChartData({
    cohort: selectedCohort,
  });

  const hasOrLoadingPerformanceGridData = performanceGridDataLoading || (performanceGridData?.length ?? 0) > 0;

  // Get cohort name from query string
  const { search: queryString } = location;
  const { reviewCycleId: queryParamReviewCycleId } = getQueryParams(queryString);

  const { data: view } = useViewById();
  const { baseCohort } = view ?? {};
  const baseCohortId = baseCohort?.uuid ?? '';

  const cohortDetails = useSelectedView();

  // TODO: add logic for access and permissions
  const REVIEW_MANAGE_AND_INSIGHTS_TAGS = [
    {
      name: 'Explore',
      route: teamBasedReviewRoute,
      search: `${queryString}`,
      accessFn: () => true,
    },
  ];

  const routesArray = REVIEW_MANAGE_AND_INSIGHTS_TAGS.map((tabData) => tabData.route);

  const reviewCycleCreationRoute = checkNewNavEnabled()
    ? staticRoutesWithCustomSidebar.reviewCycleCreation
    : routes.settings.review.reviewCycle;

  const selectedTabRouteIndex = getMatchPathIndexFromRoutesArray({
    pathname: location.pathname,
    routesArray,
  });
  const selectedTabName = REVIEW_MANAGE_AND_INSIGHTS_TAGS?.[selectedTabRouteIndex]?.name ?? '';

  /* Grid State persistence Key */
  const viewId = cohortDetails?.id;

  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.REVIEW;
    const tabId = 'Explore';
    const uniqueId = 'ReviewManageGrid';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, [viewId]);

  const { data: reviewCyclesAndCalibrationViews1, isLoading: isLoadingList } = useGetReviewCyclesAndCalibrationViews({
    cohortId: selectedCohort?.id,
  });

  const reviewCyclesAndCalibrationViews = useMemo(() => {
    const reviewCycles = isLoadingList ? [] : (reviewCyclesAndCalibrationViews1?.reviewCycles ?? []);
    const calibrationViews = isLoadingList ? [] : (reviewCyclesAndCalibrationViews1?.calibrationViews ?? []);

    return {
      reviewCycles,
      calibrationViews,
    };
  }, [reviewCyclesAndCalibrationViews1, isLoadingList]);

  const fetchPerformanceGridData = useCallback(
    ({ selectedReviewCycle: _selectedReviewCycle, cohortDetails: _cohortDetails }) => {
      setPerformanceGridDataLoading(true);
      const selectedSource = { ..._selectedReviewCycle };
      delete selectedSource.feedbackForms;
      getPerformanceGridData({
        cohort: _cohortDetails,
        groupByKeys: {
          uuid: null,
          keyName: 'employeeId',
          displayName: 'Individual',
          enabled: true,
        },
        source: selectedSource,
        type: 'rabbit',
      })
        .then((res) => {
          let perfEmpDetails = [];
          setPerformanceGridData(res.chartData);

          const rows = res.chartData.length || 0;
          const columns = res.chartData[0]?.length || 0;
          dispatch(setGridRows(rows));
          dispatch(setGridColumns(columns));

          res.chartData.flat().forEach((data) => {
            perfEmpDetails = [...perfEmpDetails, ...data.employeeDetails];
          });
          setPerformanceGridEmpDetails(perfEmpDetails);
          setIsDisplayPictures(res.isDisplayPictures);
          setPerformanceGridNoRating(res.noRating);
        })
        .catch((err) => {
          console.error('performanceGridDataErr', err);
          setPerformanceGridData([]);
        })
        .finally(() => {
          setPerformanceGridDataLoading(false);
        });
    },
    [dispatch]
  );

  const fetchPerformanceBarChartData = ({
    selectedReviewCycle: _selectedReviewCycle,
    cohortDetails: _cohortDetails,
  }) => {
    const selectedSource = { ..._selectedReviewCycle };
    delete selectedSource.feedbackForms;
    getBarGraphData({
      cohort: _cohortDetails,
      groupByKeys: {
        uuid: null,
        keyName: 'employeeId',
        displayName: 'Individual',
        enabled: true,
      },
      source: selectedSource,
    })
      .then((res) => {
        setGridChartData(res);
      })
      .catch((err) => {
        console.error('performanceGridDataErr', err);
      })
      .finally(() => {});
  };

  const fetchReviewCycleStage = useCallback(
    (_selectedReviewCycle) => {
      getReviewCycleStage(_selectedReviewCycle.uuid)
        .then((res) => {
          dispatch(setReviewCycleStage(res));
        })
        .catch((err) => console.error('Error in fetching review cycle stage :', err))
        .finally(() => {
          // setLoadingReviewCycleStage(false)
        });
    },
    [dispatch]
  );

  useEffect(() => {
    if (selectedReviewCycle && cohortDetails && selectedReviewCycle?.reviewType !== 'TRIGGERED') {
      fetchPerformanceGridData({ selectedReviewCycle, cohortDetails });
      fetchPerformanceBarChartData({ selectedReviewCycle, cohortDetails });
    }
  }, [selectedReviewCycle, cohortDetails, fetchPerformanceGridData]);

  useEffect(() => {
    if (selectedReviewCycle) {
      fetchReviewCycleStage(selectedReviewCycle);
    }
  }, [fetchReviewCycleStage, selectedReviewCycle, tableResetCounter]);

  const fetchSankeyChartData = useCallback(() => {
    const updatedReviewCycles = reviewCyclesAndCalibrationViews.reviewCycles.filter(
      (data) => data?.uuid !== selectedReviewCycle?.uuid
    );
    setReviewCycleListForSankeyChart(updatedReviewCycles);
  }, [reviewCyclesAndCalibrationViews.reviewCycles, selectedReviewCycle?.uuid, setReviewCycleListForSankeyChart]);

  useEffect(() => {
    fetchSankeyChartData();
    if (selectedReviewCycle?.reviewType === 'TRIGGERED') {
      setPerformanceGridData([]);
    }
  }, [fetchSankeyChartData, selectedReviewCycle]);

  useEffect(
    () => () => {
      dispatch(resetReviewManageSidepanelState());
    },
    [dispatch]
  );

  const handleReviewCycleChange = useCallback(() => {
    setSelectedReviewCycleForSankeyChart(null);
    fetchSankeyChartData();
    queryClient.invalidateQueries([REACT_QUERY_KEYS_REVIEWS_INSIGHTS.reviewsSankeyChart]);
  }, [fetchSankeyChartData, queryClient]);

  const isNarrowWidth =
    (!checkSankeyChartEnabled() && (selectedCohort?.type === 'MY_TEAM' || baseCohortId === '-2')) ||
    checkSankeyChartEnabled();

  return (
    <Box position="relative">
      {!isMobileView() && !checkNewNavEnabled() && (
        <PageTabbedNavigation
          tabsData={REVIEW_MANAGE_AND_INSIGHTS_TAGS}
          selectedTabName={selectedTabName}
          title={
            <TabbedNavigationTitle
              cohortName={cohortDetails?.name}
              selectedReviewCycle={selectedReviewCycle}
              queryParamReviewCycleId={queryParamReviewCycleId}
              reviewCycleAndViewOptions={reviewCyclesAndCalibrationViews}
              onReviewCycleChange={handleReviewCycleChange}
            />
          }
        />
      )}

      {checkNewNavEnabled() && (
        <Box pb={1}>
          <TabbedNavigationTitle
            cohortName={cohortDetails?.name}
            selectedReviewCycle={selectedReviewCycle}
            queryParamReviewCycleId={queryParamReviewCycleId}
            selectedCalibrationView={selectedCalibrationView}
            reviewCycleAndViewOptions={reviewCyclesAndCalibrationViews}
            onReviewCycleChange={handleReviewCycleChange}
          />
          <PageTabbedNavigationV1 tabs={tabsDeer} />
        </Box>
      )}

      <Box p={!checkNewNavEnabled() ? 4 : 0}>
        {!isMobileView() && (
          <Flex>
            {/* performance grid */}
            {hasOrLoadingPerformanceGridData && (
              <Box gap={4} mb={3} width={isNarrowWidth ? '50%' : '100%'}>
                <ExpandableContainerProvider id="perfGridContainer">
                  {performanceGridDataLoading && (
                    <PaperWithShadow p="4" h="345px">
                      {/* <EmptyStateComp performanceGridDataLoading={performanceGridDataLoading} /> */}
                      <SkeletonLoader mt="48px" />
                    </PaperWithShadow>
                  )}

                  {!performanceGridDataLoading && (
                    <PaperWithShadow p={4} h="345px">
                      <Flex justifyContent="space-between" alignItems="center" mb={3} ml="5px">
                        <TextHeader3 bold color="text.g1" ml="6px">
                          Performance grid
                        </TextHeader3>
                        <Flex bg="#EBEFF3" borderRadius="4px" mr="45px">
                          <StyledChartToggleBtn
                            variant="tertiary"
                            EndIcon={AppNavigationOutlined}
                            mr="8px"
                            className={gridOrChartToggle === 'GRID' ? 'active' : ''}
                            onClick={() => {
                              setGridOrChartToggle('GRID');
                            }}
                          />
                          <StyledChartToggleBtn
                            variant="tertiary"
                            EndIcon={BarChart}
                            className={gridOrChartToggle === 'CHART' ? 'active' : ''}
                            onClick={() => {
                              setGridOrChartToggle('CHART');
                            }}
                          />
                        </Flex>
                      </Flex>
                      {gridOrChartToggle === 'GRID' ? (
                        <ReviewManagePerformanceGrid
                          performanceGridData={performanceGridData}
                          performanceGridEmpDetails={performanceGridEmpDetails}
                          performanceGridNoRating={performanceGridNoRating}
                          isLoading={!selectedReviewCycle}
                          isDisplayPictures={isDisplayPictures}
                        />
                      ) : (
                        <PerfCalibrationBarChart data={gridChartData} isLoading={!selectedReviewCycle} />
                      )}
                    </PaperWithShadow>
                  )}
                </ExpandableContainerProvider>
              </Box>
            )}

            {/* trendline chart */}
            {(selectedCohort?.type === 'MY_TEAM' || baseCohortId === '-2') && (
              <Box
                gap={4}
                ml={hasOrLoadingPerformanceGridData ? 3 : 0}
                width={hasOrLoadingPerformanceGridData ? '50%' : '100%'}
                h="345px"
                mb={3}
              >
                <PaperWithShadow style={{ height: '100%' }} p={!trendLineChartData?.nodes ? 4 : 0}>
                  {!trendLineChartData?.nodes ? (
                    <EmptyScreen
                      illustrationSrc={NoDataImg}
                      isSmallTextSize
                      heading="During active review cycles, you can view your performance growth curve here."
                    />
                  ) : (
                    <ExpandableContainerProvider isParentDeterminesHeight id="reviewChart">
                      <PaperWithShadow p={4} h="345px">
                        <TrendLineChart trendLineChartData={trendLineChartData} isLoading={isLoading} />
                      </PaperWithShadow>
                    </ExpandableContainerProvider>
                  )}
                </PaperWithShadow>
              </Box>
            )}

            {/* sankey chart */}
            {checkSankeyChartEnabled() &&
              hasOrLoadingPerformanceGridData &&
              baseCohortId !== '-2' &&
              selectedCohort?.type !== 'MY_TEAM' &&
              selectedReviewCycle?.reviewType !== 'TRIGGERED' &&
              (selectedReviewCycle ? (
                <Box
                  gap={4}
                  ml={hasOrLoadingPerformanceGridData ? 3 : 0}
                  width={hasOrLoadingPerformanceGridData ? '50%' : '100%'}
                  h="345px"
                  mb={3}
                >
                  <ExpandableContainerProvider isParentDeterminesHeight id="reviewChart">
                    <PaperWithShadow style={{ height: '100%' }} p={4}>
                      <ReviewsSankeyChart
                        selectedReviewCycleForSankeyChart={selectedReviewCycleForSankeyChart}
                        pageSelectedReviewCycle={selectedReviewCycle}
                        setSelectedReviewCycleForSankeyChart={setSelectedReviewCycleForSankeyChart}
                        reviewCycleListForSankeyChart={reviewCycleListForSankeyChart}
                      />
                    </PaperWithShadow>
                  </ExpandableContainerProvider>
                </Box>
              ) : (
                <PaperWithShadow p={4} ml={3} h="345px">
                  <EmptyScreen
                    illustrationSrc={NoDataImg}
                    isSmallTextSize
                    heading="During active review cycles, you can view your performance growth curve here."
                  />
                </PaperWithShadow>
              ))}
          </Flex>
        )}

        {!isMobileView() && selectedReviewCycle && selectedReviewCycle?.reviewType !== 'TRIGGERED' && (
          <PaperWithShadow p={4} mb={checkIntentLandingPagesFeatureEnabled() ? 3 : 1}>
            <ReviewManageTimeline selectedReviewCycle={selectedReviewCycle} />
          </PaperWithShadow>
        )}

        {!isMobileView() && selectedReviewCycle && (
          <ReviewManageSummary
            selectedReviewCycle={selectedReviewCycle}
            gridStatePersistenceKey={gridStatePersistenceKey}
          />
        )}

        <PaperWithShadow p={4} mb={1} mt={checkNewNavEnabled() ? 0 : 3} position="relative">
          {!selectedReviewCycle ? (
            <EmptyScreen
              heading={
                selectedCohort?.type === 'MY_TEAM'
                  ? 'During active review cycles, you can view the list of your team members here.'
                  : 'Uh-oh! It seems like there are no review cycles currently created. Start creating here'
              }
              illustrationSrc={addReviewV1}
              // eslint-disable-next-line react/no-unstable-nested-components
              CTAComp={() =>
                selectedCohort?.type === 'MY_TEAM' ? null : (
                  <Button
                    StartIcon={Plus}
                    isLink
                    link={reviewCycleCreationRoute
                      .replace(':formId', 'new')
                      .replace(':step', REVIEW_CYCLE_STEPS_V1.NAME)}
                  >
                    Create New Review Cycle
                  </Button>
                )
              }
            />
          ) : (
            <ReviewManageTableV1
              cohortDetails={cohortDetails}
              performanceGridData={performanceGridData}
              perfGridRows={performanceGridData.length || 0}
              perfGridColumns={performanceGridData[0]?.length || 0}
              renderSidepanelOverviewGrid={(_performanceGridEmpDetails) => (
                <Box p={performanceGridData?.length > 0 ? 3 : 0}>
                  <ReviewManagePerformanceGrid
                    performanceGridData={performanceGridData}
                    performanceGridEmpDetails={_performanceGridEmpDetails}
                    isLoading={!selectedReviewCycle}
                    showTooltip={false}
                    isSingleEmpVersion
                  />
                </Box>
              )}
              gridStatePersistenceKey={gridStatePersistenceKey}
            />
          )}
        </PaperWithShadow>
      </Box>
    </Box>
  );
}

export default ReviewManageV1;
